import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../../components/feature/LanguageSwitcher';

export default function Hero() {
  const { t } = useTranslation();
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleWhatsAppRedirect = (language: 'es' | 'en') => {
    const phoneNumber = language === 'es' ? '16786931234' : '14705408595';
    window.open(`https://wa.me/${phoneNumber}`, '_blank');
    setShowLanguageModal(false);
  };

  return (
    <>
      <section id="inicio" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://static.readdy.ai/image/d57f52ecca312558916e136936375c65/b5c1326e9dd16071d134b9cb00e53036.png"
            alt="Soldadura profesional"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/65 to-slate-900/80"></div>
        </div>

        {/* Navigation */}
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-gradient-to-r from-gray-900 to-gray-800 shadow-lg' : 'bg-transparent'}`}>
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://static.readdy.ai/image/d57f52ecca312558916e136936375c65/3d1ca745aa0674e2a18c39b7e5b64cd1.png" 
                alt="AbrilWeldWorksPro LLC Logo" 
                className="h-12 w-auto"
              />
              <span className={`text-xl font-bold ${scrolled ? 'text-white' : 'text-white'}`}>
                AbrilWeldWorksPro LLC
              </span>
            </div>
            <div className="flex items-center gap-8">
              <button onClick={() => scrollToSection('inicio')} className={`text-base font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-white hover:text-orange-400' : 'text-white hover:text-orange-400'}`}>
                {t('nav_home')}
              </button>
              <button onClick={() => scrollToSection('sobre-nosotros')} className={`text-base font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-white hover:text-orange-400' : 'text-white hover:text-orange-400'}`}>
                {t('nav_about')}
              </button>
              <button onClick={() => scrollToSection('servicios')} className={`text-base font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-white hover:text-orange-400' : 'text-white hover:text-orange-400'}`}>
                {t('nav_services')}
              </button>
              <button onClick={() => scrollToSection('trabajos')} className={`text-base font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-white hover:text-orange-400' : 'text-white hover:text-orange-400'}`}>
                {t('nav_gallery')}
              </button>
              <button onClick={() => setShowLanguageModal(true)} className={`text-base font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-white hover:text-orange-400' : 'text-white hover:text-orange-400'}`}>
                {t('nav_contact')}
              </button>
              <LanguageSwitcher />
            </div>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 text-center">
          <div className="inline-block mb-6 px-6 py-2 bg-orange-600 rounded-full">
            <span className="text-white text-sm font-bold tracking-wider">{t('hero_badge')}</span>
          </div>
          
          <h1 className="text-6xl md:text-7xl font-extrabold text-white mb-6 leading-tight">
            {t('hero_title_1')}<br />
            <span className="text-orange-500">{t('hero_title_2')}</span> {t('hero_title_3')}<br />
            {t('hero_title_4')}
          </h1>
          
          <p className="text-xl text-gray-200 mb-12 max-w-3xl mx-auto">
            {t('hero_subtitle')}
          </p>

          <div className="flex items-center justify-center gap-4">
            <button 
              onClick={() => scrollToSection('trabajos')}
              className="px-8 py-4 bg-gradient-to-r from-orange-600 via-orange-500 to-black text-white rounded-full font-semibold hover:shadow-2xl transition-all shadow-lg flex items-center gap-2 whitespace-nowrap cursor-pointer hover:scale-105"
            >
              {t('hero_btn_gallery')}
              <i className="ri-arrow-right-line text-xl"></i>
            </button>
            <button
              onClick={() => setShowLanguageModal(true)}
              className="px-8 py-4 bg-white text-orange-600 rounded-full font-semibold hover:bg-gray-50 transition-all shadow-lg flex items-center gap-2 whitespace-nowrap cursor-pointer hover:scale-105"
            >
              <i className="ri-file-list-3-line text-xl"></i>
              {t('hero_btn_quote')}
            </button>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce">
          <i className="ri-arrow-down-line text-4xl text-white"></i>
        </div>
      </section>

      {/* Modal de selección de idioma */}
      {showLanguageModal && (
        <div 
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setShowLanguageModal(false)}
        >
          <div 
            className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl transform transition-all"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-chat-3-line text-3xl text-orange-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {t('modal_language_title')}
              </h3>
              <p className="text-gray-600">
                {t('modal_language_subtitle')}
              </p>
            </div>

            <div className="space-y-4">
              <button
                onClick={() => handleWhatsAppRedirect('es')}
                className="w-full bg-gradient-to-r from-orange-600 to-orange-700 text-white px-6 py-4 rounded-xl font-semibold hover:from-orange-700 hover:to-orange-800 transition-all whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl flex items-center justify-between group"
              >
                <span className="flex items-center">
                  <i className="ri-translate text-2xl mr-3"></i>
                  {t('contact_spanish')}
                </span>
                <span className="text-sm opacity-90">678 693 1234</span>
              </button>

              <button
                onClick={() => handleWhatsAppRedirect('en')}
                className="w-full bg-gradient-to-r from-slate-700 to-slate-800 text-white px-6 py-4 rounded-xl font-semibold hover:from-slate-800 hover:to-slate-900 transition-all whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl flex items-center justify-between group"
              >
                <span className="flex items-center">
                  <i className="ri-global-line text-2xl mr-3"></i>
                  {t('contact_english')}
                </span>
                <span className="text-sm opacity-90">470 540 8595</span>
              </button>
            </div>

            <button
              onClick={() => setShowLanguageModal(false)}
              className="w-full mt-6 text-gray-500 hover:text-gray-700 transition-colors cursor-pointer py-2"
            >
              {t('modal_cancel')}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
